import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { UserTable, type User } from './components';
import type { BranchRef } from './components/userFormModal';

type TeamPageViewProps = {
    data: User[];
    callerId: string | null;
    showBranchColumn: boolean;
    branchRefById: (id: string | null) => BranchRef | null;
    search: string;
    onSearchChange: (value: string) => void;
    onAddUser: () => void;
    onEditUser: (id: string) => void;
    onOpenPin: (id: string) => void;
    onOpenPassword: (id: string) => void;
    onDeleteUser: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

export const TeamPageView: React.FC<TeamPageViewProps> = ({
    data,
    callerId,
    showBranchColumn,
    branchRefById,
    search,
    onSearchChange,
    onAddUser,
    onEditUser,
    onOpenPin,
    onOpenPassword,
    onDeleteUser,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t('team.components.userTable.searchPlaceholder'),
            searchAria: t('team.components.userTable.searchAria'),
            addUser: t('team.components.userTable.addUser'),
            tableAria: t('team.components.userTable.tableAria'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tableAria}
            {...getNodeTestIdProp('user-table-section')}
        >
            <div
                className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('user-table-toolbar')}
            >
                <label
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto sm:shrink-0"
                    htmlFor="user-table-search"
                    {...getNodeTestIdProp('user-table-search-wrap')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:magnifier"
                    />
                    <input
                        aria-label={copy.searchAria}
                        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                        id="user-table-search"
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={copy.searchPlaceholder}
                        type="text"
                        value={search}
                        {...getNodeTestIdProp('user-table-search')}
                    />
                </label>
                <Button
                    onPress={onAddUser}
                    variant="primary"
                    {...getNodeTestIdProp('user-table-add')}
                >
                    <Icon icon="gravity-ui:plus" />
                    {copy.addUser}
                </Button>
            </div>
            <UserTable
                branchRefById={branchRefById}
                callerId={callerId}
                data={data}
                onDeleteUser={onDeleteUser}
                onEditUser={onEditUser}
                onOpenPassword={onOpenPassword}
                onOpenPin={onOpenPin}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                showBranchColumn={showBranchColumn}
                totalCount={totalCount}
                totalPages={totalPages}
            />
        </section>
    );
};
